/**
 * The releases view — what each deployment environment serves, how it got
 * there, and the URL that reaches it (task 760; renamed from `RolloutView` and
 * given the URL list in task 799).
 *
 * Report: `project/reports/innovate-service-deployment-environments.md`.
 *
 * Two parts answering two different questions:
 *
 *   1. **The diagram** — one lane per environment in promotion order: what is
 *      serving there now, since when, who put it there, **and the URL that
 *      reaches it**. Promotion arrows are drawn from the deployment history,
 *      never inferred from version numbers: an arrow means "this exact version
 *      served in the previous lane first", which is the whole claim promotion
 *      makes. The plain URL heads the diagram, because what it means depends on
 *      how the AI Service is served.
 *   2. **The versions** — published versions newest-first, each expanding to the
 *      pin set it froze. `PinSetList` keeps live references visually distinct
 *      from pinned versions, because a pin set must never look like it freezes
 *      more than it does.
 *
 * **THE URLS MOVED INTO THE DIAGRAM (task 819).** They used to be a standalone
 * `EnvironmentUrlList` mounted below a "How this is served" card. That listed
 * the same environments the lanes already showed, in a second shape directly
 * beneath them — the same information twice — so kv's review asked for one: the
 * URL belongs on the card for the environment it addresses. `EnvironmentUrlList`
 * itself is untouched and still mounted by the API Docs and Developer Info tabs,
 * where it is the only thing answering "which URL do I call?".
 *
 * The "How this is served" card went with it, and so — eventually — did the
 * serving-mode action it carried; see the serving-mode note below.
 *
 * **AND THE PLAIN URL CAME BACK OUT (task 823).** The task numbered 819 also put
 * a copyable plain URL in that header; kv's next review removed it, and the
 * reasoning holds: every lane already carries the URL that reaches it, and the
 * bare one is the single address whose meaning depends on how the service is
 * served — the one an integrator can copy and be wrong about. Accounts with no
 * environments are unaffected — they never reached this diagram, then or now,
 * because the unavailable state returns first.
 *
 * The lanes themselves are the page's subject and used to be the quietest thing
 * on it, so task 823 also made them larger and split them by state: a serving
 * lane takes the green family this app uses for published/serving, an empty one
 * stays muted behind a dashed edge.
 *
 * Every URL still comes from `utils/endpointUrl`, never composed here, so this
 * cannot drift from what the app links, tests and curls elsewhere.
 *
 * Promote and roll back are the SAME write — pointing an environment at a
 * version — so they share one confirmation dialog and one mutation. Only the
 * wording differs, and it is chosen by comparing against what is serving now.
 *
 * The `rollout-*` test ids are kept on purpose, for the same reason the flag
 * name is: they are internal identifiers, and churning them buys nothing.
 *
 * **ACTIONS LEFT THE VERSION ROWS, AND THE LOG CAME OUT (task 832).** Two
 * surviving changes from the report
 * `project/reports/innovate-environments-paths-and-release-controls.md` (its
 * third, a Serving mode card, is gone — see below):
 *
 *   1. **Version rows state facts; lanes perform actions.** A published-version
 *      row used to carry one chip per environment — a disabled "Serving prod"
 *      beside an actionable "→ staging", mixing what is true with what you could
 *      do. The `→ {env}` affordances are gone; only quiet serving badges remain.
 *      The deploy they performed moves into the lane it targets, as a version
 *      picker beside the lane's own URL, which is where promote and roll back
 *      already lived.
 *   2. **The release log** — the append-only deployment history has been in the
 *      API since task 760 and drew the promotion arrows here, but nothing ever
 *      SHOWED it. It is now a section at the foot of the view: version →
 *      environment, by whom, when, notes. Last, not under the lanes, because it
 *      reads in that order — what serves now, what is published, what happened.
 *      It is sorted newest-first here rather than trusting the read's order: the
 *      claim the section makes is about time, so it should not be an accident of
 *      the endpoint.
 *
 * **AND THE SERVING-MODE SWITCH IS GONE (task 853).** The chip, the two-option
 * card and its confirmation dialog are removed, and with them this view's only
 * write to the endpoint — the app writes no serving mode anywhere now, and
 * since task 869 there is no such field to write. The choice had nothing left
 * to decide: with Publishing OFF the service refused the environment-served
 * option outright, so the switch was dead; with Publishing ON the account has
 * stages, the environment holding `/` is what the bare URL serves, and the
 * other option only invited a User to move production traffic off those stages
 * by accident. So serving is a function of the ACCOUNT's publishing setting,
 * which this view already reads through `useVersioningEnabled` and already
 * requires before rendering anything below.
 *
 * The editing note the diagram header carried went with it: it said editing
 * takes effect immediately, which is only true with Publishing off — and with
 * Publishing off the unavailable state returns first and says so itself. It had
 * no reachable state left.
 *
 * **THE DEFAULT `/` LANE IS DRAWN EVEN WITH NO ROW FOR IT (task 871).** An AI
 * Service with published versions on an account holding no environment rows
 * used to reach the "no deployment environments yet" state and stop — with its
 * versions listed on the same screen and no way to release any of them. The
 * bare URL was serving the newest publish the whole time. So this view
 * synthesizes ONE lane for path `/` in exactly that case (see `ReleaseLane`),
 * rendered through the same card as every real lane, and releases to it by path
 * like everything else; innovate-service creates the row alongside the
 * deployment, after which nothing here is synthesized any more.
 *
 * **AND NOTHING IS "LIVE" OR "MANAGED" ON SCREEN (task 871, kv's ruling).**
 * Those were the names of the retired switch's two options. What is true is
 * said positively: a version is RELEASED to a lane — `Released: / v1.0`, in the
 * badge beside the version number — and an AI Service with nothing released
 * serves its WORKING STATE.
 *
 * **AND AN UNPUBLISHED SERVICE SAYS SO, INSTEAD OF DRAWING EMPTY LANES
 * (task 1014).** The serving rule (task 869) is that an AI Service with NO
 * published version serves its working rows at the bare URL, whatever the
 * account setting and whatever stages exist. The lane strip said the opposite:
 * one lane per stage, every one of them "Nothing deployed", directly above a
 * sentence saying the service serves its working state. So while Publishing is
 * on and nothing is published, the strip is replaced by ONE card
 * (`WorkingStateCard`) naming what the bare URL answers with, carrying that
 * URL, and listing the stages as waiting rather than empty. It takes the INDIGO
 * family the Bare URL badge already uses, never the green serving-a-release
 * family, so served-but-unreleased cannot be mistaken for a released lane.
 *
 * That card also absorbed the "no deployment environments yet" half of the
 * unavailable state: an account with no stages and nothing published reaches
 * the same card, with the Settings pointer as its footer. What is left of
 * `rollout-unavailable` is the one state it still answers — Publishing off.
 *
 * **THE DEPLOY TOAST REPORTS, IT DOES NOT GUESS (task 858).** A failed deploy
 * used to render one hardcoded sentence for every outcome — 422, 403, 404, 500
 * alike — offering two guesses at the cause: that the version might not be
 * published, or the environment removed. On the prd account that prompted this,
 * both were false and the service's own refusal, which names the lane to go
 * through, never reached the screen. `confirmDeploy`'s `onError` now takes the
 * error and reads the shape the interceptor already produces (see
 * `deployRefusalReason`), so what the User is told is what the service said.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useVersioningEnabled, useResourceVersions } from '@/hooks/useResourceVersions';
import {
  useCreateDeployment,
  useCurrentDeployments,
  useDeploymentHistory,
  useEnvironments,
} from '@/hooks/useEnvironments';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { useOptionalToast } from '@/contexts/ToastContext';
import { PinSetList } from '@/components/versioning/PinSetList';
import { UserName } from '@/components/ui/UserName';
import {
  customDomainUrl,
  environmentDisplayPath,
  environmentInvokeUrl,
} from '@/utils/endpointUrl';
import { formatRelativeTime } from '@/utils/dateUtils';
import { flattenPinSet } from '@/types/versioning';
import {
  BARE_URL_ENVIRONMENT_PATH,
  deploymentsByEnvironment,
  versionLabel,
} from '@/types/environments';
import type { Endpoint } from '@/types';
import type { Deployment, Environment } from '@/types/environments';
import type { ResourceVersionSummary } from '@/types/versioning';

interface ReleasesViewProps {
  endpoint: Endpoint;
  /**
   * The caller's own versions controls, hosted by the Published versions block
   * (task 823) — the AI Service tab's version-status chip and Version history
   * link, which used to sit in a second card of its own above this view.
   *
   * Passed in rather than built here because versioning ADOPTION is the
   * caller's concern: this view knows what is deployed, not whether the account
   * has taken up Publishing. Rendered in the unavailable state too, so hiding
   * that card never takes the chip with it.
   */
  versionsToolbar?: ReactNode;
}

/**
 * A lane as the diagram draws it (task 871).
 *
 * Almost always an environment row, one-for-one. The exception is the DEFAULT
 * `/` LANE on an account that has no environment rows at all: the bare URL
 * still serves something — the newest published version — so the lane exists as
 * a fact whether or not a row has been created for it, and the view says so
 * rather than sending the User to Settings to create the row by hand.
 *
 * The card is driven off this shape rather than off `Environment` so both kinds
 * render through exactly one piece of markup. What separates them is the id: a
 * synthesized lane has none, which is precisely why it cannot be looked up in
 * the deployment reads, and why releasing to it goes BY PATH like every other
 * release does.
 */
interface ReleaseLane {
  /** Stable React key and DOM id fragment. */
  key: string;
  /** The environment row's id — null while no row exists for this lane yet. */
  environmentId: string | null;
  name: string;
  path: string;
  /** True for the default `/` lane the account has no row for yet. */
  synthesized: boolean;
}

/**
 * What innovate-service calls the `/` environment it creates on first publish.
 * Used only to label the synthesized lane, so the name does not change under
 * the User the moment the real row arrives.
 */
const DEFAULT_LANE_NAME = 'Production';

/** What a pending deploy is about to do, and what to call it. */
interface PendingDeploy {
  lane: ReleaseLane;
  version: string;
  /** Rollback when the environment already served something newer. */
  kind: 'deploy' | 'promote' | 'rollback';
}

const ACTION_COPY: Record<PendingDeploy['kind'], { title: string; verb: string; body: string }> = {
  deploy: {
    title: 'Deploy',
    verb: 'Deploy',
    body: 'This environment has served nothing so far. It will start serving this version immediately.',
  },
  promote: {
    title: 'Promote',
    verb: 'Promote',
    body: 'The version this environment currently serves keeps running until the moment this completes.',
  },
  rollback: {
    title: 'Roll back',
    verb: 'Roll back',
    body: 'This points the environment at an earlier version. Nothing is deleted — the newer version stays published and can be deployed again.',
  },
};

/**
 * The one case `ACTION_COPY.deploy` would state wrongly (task 871): the
 * synthesized default lane HAS been serving something — the newest published
 * version — so "has served nothing so far" would be a lie. Saying what actually
 * changes is also what makes the choice worth making: after this the lane holds
 * the version named here and stops following each new publish.
 */
const SYNTHESIZED_LANE_BODY =
  'The bare URL currently serves whichever version was published last. Releasing pins it to this version until another is released.';

/**
 * Why the deploy was refused, IN THE SERVICE'S OWN WORDS (task 858).
 *
 * The axios response interceptor (`src/services/api.ts`) already flattens the
 * rejection onto the Error: `.message` is the service's `error` string and
 * `.validationErrors` its `details.validationErrors`. The shape is typed here
 * and nowhere else on purpose — it is read once, in one place, so this stays a
 * local reading of an existing contract rather than a shared interface or a
 * mapping layer.
 *
 * Order: the FIELD message first, because it is the one line that names the
 * next action ("deploy it there first"); then the top-level message; then a
 * fallback that says only that there was no reason, since the whole point of
 * this function is that the toast stops inventing one.
 */
const deployRefusalReason = (error: unknown): string => {
  const err = (error ?? {}) as {
    message?: string;
    status?: number;
    validationErrors?: Array<{ field?: string; message?: string }>;
  };

  const field = err.validationErrors?.find((entry) => entry.message?.trim())?.message?.trim();
  if (field) return field;

  const top = typeof err.message === 'string' ? err.message.trim() : '';
  if (top) return top;

  return 'The service refused the deploy and gave no reason.';
};

/**
 * The label the card's copy affordance reports itself by, in the toast and in
 * the brief confirm tick — the same `handleCopy(url, label)` contract the lanes
 * use, so there is one copy behaviour on this view and not two.
 */
const WORKING_STATE_COPY_LABEL = 'Working state';

interface WorkingStateCardProps {
  endpoint: Endpoint;
  /** The account's stages, in promotion order. Empty is a supported state. */
  environments: Environment[];
  /** The hostname the account serves on, when it has one (task 857). */
  primaryHostname: string | null;
  copied: string | null;
  onCopy: (url: string, label: string) => void;
}

/**
 * SERVING THE WORKING STATE (task 1014).
 *
 * What stands where the lane strip stands while Publishing is on and the AI
 * Service has no published version. Every fact on it is already loaded by the
 * view; the card adds no read and no action — Publish stays in the page header,
 * which is what the footer points at.
 *
 * The stages are LISTED rather than drawn as lanes on purpose: they are real
 * and the User configured them, so they are named with their addresses, but an
 * empty lane reads as "this service is down" and that is precisely the claim
 * this card exists to stop making.
 */
function WorkingStateCard({
  endpoint,
  environments,
  primaryHostname,
  copied,
  onCopy,
}: WorkingStateCardProps) {
  // `null`, not `/`: the bare URL is the address with no environment segment,
  // which is the one this state serves. Both spellings compose to the same
  // string through `qualifiedEndpointPath`, and this is the honest one.
  const platformUrl = environmentInvokeUrl(endpoint, null);
  const bareUrl = primaryHostname ? customDomainUrl(endpoint, primaryHostname, {}) : platformUrl;

  return (
    <div
      data-testid="releases-working-state"
      className="mt-4 rounded-lg border border-l-4 border-indigo-200 border-l-indigo-500 bg-indigo-50 p-4 dark:border-indigo-800 dark:border-l-indigo-500 dark:bg-indigo-900/20"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-base font-semibold text-gray-900 dark:text-white">
          Serving the working state
        </span>
        <span
          data-testid="releases-working-state-badge"
          className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
        >
          Bare URL
        </span>
      </div>

      <p className="mt-2 text-sm text-indigo-900 dark:text-indigo-100">
        This AI Service has not been published yet, so its bare URL answers with{' '}
        <span className="font-semibold">the service as it is now</span>. Every edit you save takes
        effect on the next call — exactly as it did before Publishing was turned on.
      </p>

      <div className="mt-3 flex items-start justify-between gap-3 rounded-md border border-indigo-200 bg-white px-3 py-2 dark:border-indigo-800 dark:bg-gray-800">
        <p
          data-testid="releases-working-state-url"
          className="min-w-0 break-all font-mono text-xs text-gray-900 dark:text-gray-100"
        >
          {bareUrl}
        </p>
        <button
          type="button"
          onClick={() => onCopy(bareUrl, WORKING_STATE_COPY_LABEL)}
          data-testid="releases-working-state-url-copy"
          aria-label="Copy the bare URL"
          className="inline-flex flex-shrink-0 items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {copied === WORKING_STATE_COPY_LABEL ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* The platform address, beneath the one the account publishes on
          (task 857) — absent entirely without a custom domain, where the URL
          above already IS this one. */}
      {primaryHostname && (
        <div className="mt-1 flex items-start justify-between gap-3 px-3">
          <p
            data-testid="releases-working-state-url-platform"
            className="min-w-0 break-all font-mono text-[11px] text-gray-500 dark:text-gray-400"
          >
            {platformUrl}
          </p>
          <button
            type="button"
            onClick={() => onCopy(platformUrl, `${WORKING_STATE_COPY_LABEL} platform`)}
            data-testid="releases-working-state-url-platform-copy"
            aria-label="Copy the bare platform URL"
            className="inline-flex flex-shrink-0 items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* POINTS AT PUBLISH, DOES NOT OFFER IT. One action, one place: the page
          header owns it. With no stages, the pointer this card replaced —
          Settings → Account → Deployment environments — is what belongs here
          instead, because that is the next thing such an account can do. */}
      <p data-testid="releases-working-state-footer" className="mt-3 text-xs text-gray-600 dark:text-gray-400">
        Use <span className="font-semibold text-gray-900 dark:text-white">Publish</span> in the page
        header to freeze a version.{' '}
        {environments.length > 0 ? (
          <>
            Once one exists, this card becomes the lanes below, and the bare URL serves only what has
            been released.
          </>
        ) : (
          <>
            To promote versions through named stages, add them under{' '}
            <span className="font-medium text-gray-900 dark:text-white">
              Settings → Account → Deployment environments
            </span>
            .
          </>
        )}
      </p>

      {environments.length > 0 && (
        <div
          data-testid="releases-working-state-stages"
          className="mt-3 border-t border-indigo-200 pt-3 dark:border-indigo-800"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Your stages
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {environments.map((environment) => (
              <li
                key={environment.id}
                data-testid="releases-working-state-stage"
                data-environment={environment.name}
                data-path={environment.path}
                className="inline-flex flex-wrap items-center gap-2 rounded-md border border-dashed border-indigo-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 dark:border-indigo-800 dark:bg-gray-800 dark:text-gray-300"
              >
                <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">
                  {environment.name}
                </span>
                <span className="min-w-0 break-all font-mono text-gray-400 dark:text-gray-500">
                  {environmentDisplayPath(endpoint, environment.path)}
                </span>
                <span className="text-gray-400 dark:text-gray-500">
                  {/* The `/` holder is the one stage whose first release moves
                      the bare URL off the working state — the others only ever
                      gain an address of their own. */}
                  {environment.path === BARE_URL_ENVIRONMENT_PATH
                    ? '· will take over the bare URL on first release'
                    : '· waiting for a release'}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Stage URLs answer <span className="font-mono">404</span> until a published version is
            released to them. They never serve the working state.
          </p>
        </div>
      )}
    </div>
  );
}

export function ReleasesView({ endpoint, versionsToolbar }: ReleasesViewProps) {
  // The lever keeps its original `…rollout` name even though the surface is now
  // called Releases (task 799): renaming a live flag would touch the manifest,
  // Unleash and the generated types for no behavior change.
  const featureEnabled = useFlag('innovate.fe.environments.rollout');
  const { enabled: publishingEnabled, isResolved: publishingResolved } = useVersioningEnabled();
  const { environments } = useEnvironments();
  const { data: current = [] } = useCurrentDeployments(endpoint.id);
  const { data: history = [] } = useDeploymentHistory(endpoint.id);
  // `isLoading` as well as the data (task 1014): the versions read only starts
  // once the account setting has resolved, so on the first render past the
  // guard below `versions` is ALWAYS empty — and rendering the working-state
  // card off that would flash "not published yet" at every published service.
  const { data: versions = [], isLoading: versionsLoading } = useResourceVersions(
    'endpoint',
    endpoint.id
  );
  const createDeployment = useCreateDeployment(endpoint.id);
  // The hostname each lane's URL leads with, when the account has one serving
  // (task 857). None → the lanes carry exactly the platform URLs they always did.
  const customDomains = useActiveCustomDomainHostnames();
  const primaryHostname = customDomains[0] ?? null;
  const toast = useOptionalToast();

  const [expanded, setExpanded] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingDeploy | null>(null);
  /** Which URL was just copied, so its button can confirm briefly (task 819). */
  const [copied, setCopied] = useState<string | null>(null);

  const currentByEnvironment = useMemo(() => deploymentsByEnvironment(current), [current]);

  /**
   * Which versions each environment has EVER served, from history. This is what
   * makes a promotion arrow a fact rather than a guess.
   */
  const servedByEnvironment = useMemo(() => {
    const served: Record<string, Set<string>> = {};
    for (const row of history) {
      if (!row.resourceVersion) continue;
      served[row.environmentId] ??= new Set<string>();
      served[row.environmentId].add(versionLabel(row.resourceVersion));
    }
    return served;
  }, [history]);

  /**
   * The release log's rows: the same history, newest first (task 832). Sorted
   * here rather than trusted from the read, because "newest first" is the claim
   * the section makes. The sort is stable, so rows sharing a timestamp keep the
   * order the service returned them in.
   */
  const releaseLog = useMemo(
    () =>
      [...history].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [history]
  );

  /**
   * Environment names by id, for the log. A history row carries the environment
   * it deployed to, but the live list is the better source when both exist —
   * and a row whose environment has since been deleted has no entry in either,
   * which the log says out loud rather than rendering a blank.
   */
  const environmentNameById = useMemo(() => {
    const names: Record<string, string> = {};
    for (const environment of environments) names[environment.id] = environment.name;
    return names;
  }, [environments]);

  if (!featureEnabled) return null;

  const publishedVersions = versions.filter((version) => version.status === 'published');

  /**
   * The newest published version, by number rather than by list order — this is
   * what the bare URL answers with when no release has pinned it (task 871), so
   * it must not be an accident of how the versions read happened to sort.
   */
  const newestPublished =
    publishedVersions.length > 0
      ? [...publishedVersions].sort((a, b) => b.major - a.major || b.minor - a.minor)[0].version
      : null;

  /**
   * The lanes to draw (task 871). Ordinarily one per environment row, in the
   * promotion order the account set. With NO rows at all and something
   * published, one synthesized default lane stands in for the `/` environment
   * innovate-service has not had a reason to create yet — because the bare URL
   * is serving that newest version regardless, and the User needs to be able to
   * pick a different one. Never both: a real list already describes `/` if the
   * account holds it, and a synthesized lane on top would invent an address.
   */
  const lanes: ReleaseLane[] =
    environments.length > 0
      ? environments.map((environment) => ({
          key: environment.id,
          environmentId: environment.id,
          name: environment.name,
          path: environment.path,
          synthesized: false,
        }))
      : newestPublished
        ? [
            {
              key: 'default-lane',
              environmentId: null,
              name: DEFAULT_LANE_NAME,
              path: BARE_URL_ENVIRONMENT_PATH,
              synthesized: true,
            },
          ]
        : [];

  // Until the account setting has resolved — and, where it is on, until the
  // versions read has answered — say NOTHING about why: the explanations below
  // are mutually exclusive claims, and rendering any one of them on the way to
  // the truth would flash a wrong reason at the User.
  if (!publishingResolved || (publishingEnabled && versionsLoading)) {
    return (
      <p
        data-testid="rollout-loading"
        className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
      >
        Loading deployment environments…
      </p>
    );
  }

  /**
   * NOTHING PUBLISHED, SO THE BARE URL IS ANSWERING WITH THE WORKING STATE
   * (task 1014). True whatever stages the account holds: the serving rule
   * (task 869) does not consult them until a version exists to release.
   */
  const servingWorkingState = publishingEnabled && publishedVersions.length === 0;

  // ONE state left here (task 1014): Publishing off. The "no deployment
  // environments yet" half used to share this return, but an account with no
  // stages and nothing published is an account serving its working state, and
  // the card says that — carrying this pointer as its footer.
  if (!publishingEnabled) {
    return (
      <div
        data-testid="rollout-unavailable"
        className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
      >
        {/* The caller's versions controls belong here too (task 823): this
            state is exactly where an account has the least else on the tab,
            and the chip is the only thing telling it where versioning stands. */}
        {versionsToolbar && (
          <div className="mb-4 flex flex-wrap items-center justify-end gap-3 border-b border-gray-100 pb-3 dark:border-gray-700">
            {versionsToolbar}
          </div>
        )}
        {/* NOT "edit-and-it's-live" (task 871, kv's ruling). "Live" was one
            half of a switch that no longer exists, and it described nothing
            a User could act on. The working state is the thing itself: what
            the AI Service serves until a version is published and
            released. */}
        Deployment environments need Publishing turned on for the account. Until then this AI
        Service serves its working state — every edit you save takes effect immediately, which
        is exactly how it behaves today.
      </div>
    );
  }

  const deployedLabel = (deployment: Deployment | undefined): string | null =>
    deployment?.resourceVersion ? versionLabel(deployment.resourceVersion) : null;

  /** The deployment pointing at this lane, if the lane has a row to point at. */
  const laneDeployment = (lane: ReleaseLane): Deployment | undefined =>
    lane.environmentId ? currentByEnvironment[lane.environmentId] : undefined;

  /**
   * What the lane serves. A released version when one has been deployed there;
   * otherwise, for the synthesized default lane only, the newest published
   * version — which is what the bare URL genuinely answers with (task 871).
   */
  const laneServing = (lane: ReleaseLane): string | null =>
    deployedLabel(laneDeployment(lane)) ?? (lane.synthesized ? newestPublished : null);

  /**
   * The versions the sequential rule would accept for a lane (task 1134), from
   * the published versions and the release history this view already loads:
   *
   *   1. the first lane takes every published version;
   *   2. any other lane takes what has EVER served in the lane above it, plus
   *   3. what this lane has served before (a roll-back).
   *
   * The service stays the authority and refuses anything else; this only stops
   * the picker offering what would be refused.
   */
  const eligibleVersions = (lane: ReleaseLane, index: number): ResourceVersionSummary[] => {
    if (index === 0) return publishedVersions;
    const aboveId = lanes[index - 1]?.environmentId;
    const aboveServed = aboveId ? servedByEnvironment[aboveId] : undefined;
    const ownServed = lane.environmentId ? servedByEnvironment[lane.environmentId] : undefined;
    return publishedVersions.filter(
      (version) => aboveServed?.has(version.version) === true || ownServed?.has(version.version) === true
    );
  };

  const openDeploy = (lane: ReleaseLane, version: string) => {
    const serving = deployedLabel(laneDeployment(lane));
    const previousLane = lanes[lanes.indexOf(lane) - 1];
    const previousLaneId = previousLane?.environmentId;
    const kind: PendingDeploy['kind'] =
      !serving || !lane.environmentId
        ? 'deploy'
        : // Rolling back is naming a version this environment has already moved
          // past; promoting is taking one the lane before it has exercised.
          servedByEnvironment[lane.environmentId]?.has(version)
          ? 'rollback'
          : previousLaneId && servedByEnvironment[previousLaneId]?.has(version)
            ? 'promote'
            : 'deploy';

    setPending({ lane, version, kind });
  };

  const confirmDeploy = () => {
    if (!pending) return;
    createDeployment.mutate(
      // BY PATH, not by display label (task 830): the service resolves this
      // field against `Environment.path`, and a name is free text that two stages
      // may share. It is also what lets the synthesized default lane release at
      // all (task 871) — it has no id to send, and `/` is enough for the service
      // to create the row alongside the deployment.
      { environment: pending.lane.path, version: pending.version },
      {
        onSuccess: () => {
          toast?.success(
            `${pending.lane.name} now serves ${pending.version}`,
            'Traffic to that environment moved immediately.'
          );
          setPending(null);
        },
        onError: (error) => {
          toast?.error('Could not deploy', deployRefusalReason(error));
          setPending(null);
        },
      }
    );
  };

  /** The same clipboard + toast pattern the URL list used (task 819). */
  const handleCopy = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(label);
      toast?.success(`${label} URL copied to clipboard`, url);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast?.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-6" data-testid="rollout-view">
      {/* THE DIAGRAM (task 819): the plain URL, then the lanes it promotes
          through — one block, because they are one story about where a request
          goes. */}
      <div
        data-testid="rollout-diagram"
        className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
      >
        {/* THE HEADER, WITHOUT THE PLAIN URL (task 823).

            The task numbered 819 put a copyable plain URL here. kv's next review
            took it back out, and the reasoning is sound: every lane below
            already carries the URL that reaches it, and the bare one is the
            single address whose meaning depends on how the service is served —
            the one an integrator can copy and be wrong about.

            The two things that row also carried have since gone (task 853): the
            mode toggle, because serving is the account's publishing setting and
            not a per-service choice; and the live-editing note, because it was
            only ever true with publishing OFF, which the unavailable state
            above returns for first. What is left is the section's own title. */}
        <div
          data-testid="rollout-diagram-header"
          className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700"
        >
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Environments</h3>
        </div>

        {/* THE CARD STANDS WHERE THE STRIP STANDS (task 1014) — same block, same
            header, because it answers the same question: what does a request
            reach? Until a version is published the honest answer is "the
            service as it is now", at one address. */}
        {servingWorkingState ? (
          <WorkingStateCard
            endpoint={endpoint}
            environments={environments}
            primaryHostname={primaryHostname}
            copied={copied}
            onCopy={handleCopy}
          />
        ) : (
        <>
        {/* Lanes, in promotion order */}
        <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="rollout-lanes">
        {lanes.map((lane, index) => {
          const deployment = laneDeployment(lane);
          const serving = laneServing(lane);
          const previousLaneId = lanes[index - 1]?.environmentId;
          const promotedFromPrevious =
            index > 0 &&
            serving !== null &&
            !!previousLaneId &&
            servedByEnvironment[previousLaneId]?.has(serving) === true;
          const eligible = eligibleVersions(lane, index);
          const laneAbove = lanes[index - 1];
          const platformLaneUrl = environmentInvokeUrl(endpoint, lane.path);
          const laneUrl = primaryHostname
            ? customDomainUrl(endpoint, primaryHostname, { environment: lane.path })
            : platformLaneUrl;

          return (
            <div key={lane.key} className="flex min-w-0 flex-shrink-0 items-center gap-3">
              {index > 0 && (
                <ArrowRightIcon
                  aria-hidden="true"
                  data-testid={promotedFromPrevious ? 'promotion-arrow' : 'promotion-arrow-idle'}
                  className={`h-5 w-5 flex-shrink-0 ${
                    promotedFromPrevious
                      ? 'text-indigo-500'
                      : 'text-gray-200 dark:text-gray-700'
                  }`}
                />
              )}

              <div
                data-testid="rollout-lane"
                data-environment={lane.name}
                // So a test — and a reader of the DOM — can tell the lane that
                // stands in for an uncreated `/` row from one backed by a real
                // environment (task 871).
                data-synthesized={lane.synthesized ? 'true' : 'false'}
                // `data-serving` so the two states can be asserted without
                // pinning a Tailwind class list (task 823).
                data-serving={serving ? 'true' : 'false'}
                // BIGGER, AND COLOR-SPLIT BY STATE (task 823). The lanes are the
                // point of this page and were the quietest thing on it. A lane
                // that serves something takes the GREEN family this app already
                // uses for published/serving (`VersionStatusChip`); an empty one
                // stays muted with a dashed edge, so "nothing here yet" reads as
                // a state rather than a card that failed to load. The left
                // accent bar is the idiom the environment URL rows use.
                className={`w-72 rounded-lg border border-l-4 p-4 ${
                  serving
                    ? 'border-green-300 border-l-green-500 bg-green-50 dark:border-green-700 dark:border-l-green-500 dark:bg-green-900/20'
                    : 'border-dashed border-gray-300 border-l-gray-300 bg-gray-50 dark:border-gray-600 dark:border-l-gray-600 dark:bg-gray-800/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  {/* The environment name is what a reader scans this row for,
                      so it is the dominant element rather than one more small
                      line among several. */}
                  <span
                    className={`truncate font-mono text-base font-semibold ${
                      serving
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {lane.name}
                  </span>
                  {/* Where the `Default` badge was (task 831). The lane that
                      holds path `/` is the one the bare URL reaches, which is
                      the fact the retired boolean carried — said now in terms of
                      the address, because that is all it ever meant. */}
                  {lane.path === BARE_URL_ENVIRONMENT_PATH && (
                    <span
                      data-testid="lane-bare-url-badge"
                      className="flex-shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                    >
                      Bare URL
                    </span>
                  )}
                </div>

                {serving ? (
                  <div className="mt-2" data-testid="lane-current">
                    <div className="text-sm text-green-900 dark:text-green-100">
                      Serving <span className="font-semibold">v{serving}</span>
                    </div>
                    {deployment ? (
                      <>
                        <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                          {formatRelativeTime(deployment.createdAt)}
                          {deployment.createdBy ? (
                            <>
                              {' · '}
                              <UserName userId={deployment.createdBy} />
                            </>
                          ) : ''}
                        </div>
                        {deployment.notes && (
                          <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                            {deployment.notes}
                          </p>
                        )}
                      </>
                    ) : (
                      // Nothing was released here — the lane is following each
                      // publish, which is a different fact from a release and
                      // has no author or timestamp to show (task 871).
                      <div
                        data-testid="lane-follows-newest"
                        className="mt-0.5 text-xs text-gray-600 dark:text-gray-400"
                      >
                        The version published most recently. Release one to pin it here.
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-400 dark:text-gray-500" data-testid="lane-empty">
                    Nothing deployed
                  </p>
                )}

                {/* The URL that reaches THIS environment (task 819). Shown even
                    when nothing is deployed: calling it is a valid way to
                    discover that state, and the list this replaces showed those
                    environments too. */}
                <div
                  className={`mt-3 border-t pt-2 ${
                    serving
                      ? 'border-green-200 dark:border-green-800'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <p
                    data-testid="lane-url-path"
                    className="truncate font-mono text-[11px] text-gray-500 dark:text-gray-400"
                  >
                    {environmentDisplayPath(endpoint, lane.path)}
                  </p>
                  <div className="mt-1 flex items-start justify-between gap-2">
                    <p
                      data-testid="lane-url-value"
                      className="min-w-0 break-all font-mono text-[11px] text-gray-700 dark:text-gray-300"
                    >
                      {laneUrl}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleCopy(laneUrl, lane.name)}
                      data-testid="lane-url-copy"
                      aria-label={`Copy ${lane.name} URL`}
                      className="inline-flex flex-shrink-0 items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                      {copied === lane.name ? (
                        <CheckIcon className="h-4 w-4 text-green-500" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {/* The platform address, beneath the one the account
                      publishes on (task 857). Absent entirely without a custom
                      domain, where the URL above already IS this one. */}
                  {primaryHostname && (
                    <div className="mt-1 flex items-start justify-between gap-2">
                      <p
                        data-testid="lane-url-platform"
                        className="min-w-0 break-all font-mono text-[11px] text-gray-500 dark:text-gray-400"
                      >
                        {platformLaneUrl}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleCopy(platformLaneUrl, `${lane.name} platform`)}
                        data-testid="lane-url-platform-copy"
                        aria-label={`Copy ${lane.name} platform URL`}
                        className="inline-flex flex-shrink-0 items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* DEPLOYING HAPPENS HERE NOW (task 832). The `→ {env}` chips
                    left the version rows, so the lane owns every write that
                    points it somewhere: this picker for a direct deploy, and the
                    same confirmation dialog promote and roll back already use —
                    which is what decides, from history, which of the three this
                    is. The version already serving is not offered: deploying it
                    again would change nothing. */}
                {publishedVersions.length > 0 && (
                  <div
                    className={`mt-3 border-t pt-2 ${
                      serving
                        ? 'border-green-200 dark:border-green-800'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <label
                      htmlFor={`lane-version-picker-${lane.key}`}
                      className="sr-only"
                    >
                      Deploy a version to {lane.name}
                    </label>
                    {/* ONLY WHAT THE RULE WOULD ACCEPT (task 1134). A lane after
                        the first offers what the lane above it has served, plus
                        its own roll-backs; with nothing eligible the control is
                        disabled and says which lane comes first. */}
                    {eligible.length === 0 && laneAbove ? (
                      <select
                        id={`lane-version-picker-${lane.key}`}
                        data-testid="lane-version-picker"
                        data-environment={lane.name}
                        data-eligible="none"
                        disabled
                        value=""
                        className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400"
                      >
                        <option value="">Nothing eligible — release to {laneAbove.name} first.</option>
                      </select>
                    ) : (
                    <select
                      id={`lane-version-picker-${lane.key}`}
                      data-testid="lane-version-picker"
                      data-environment={lane.name}
                      // Always shows the prompt: this is an action menu, not a
                      // field holding a value — the lane's value is what it
                      // serves, stated above.
                      value=""
                      onChange={(event) => {
                        const chosen = event.target.value;
                        if (chosen) openDeploy(lane, chosen);
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    >
                      <option value="">Deploy a version…</option>
                      {eligible
                        // A lane that has actually been released to hides the
                        // version it already serves: releasing it again would
                        // change nothing. The synthesized default lane hides
                        // nothing (task 871) — it has released NOTHING, so
                        // picking the newest is the real act of pinning the bare
                        // URL to it rather than letting it follow each publish.
                        .filter((version) => !deployment || version.version !== serving)
                        .map((version) => (
                          <option key={version.id} value={version.version}>
                            v{version.version}
                          </option>
                        ))}
                    </select>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
        </>
        )}
      </div>

      {/* Published versions, newest first — and since task 823 the ONLY such
          block on the screen. The AI Service tab's own card above this one said
          the same heading twice; its chip and history link arrive here through
          `versionsToolbar`. */}
      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div
          data-testid="rollout-versions-header"
          className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700"
        >
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Published versions</h3>
          {versionsToolbar && (
            <div className="flex flex-wrap items-center gap-3">{versionsToolbar}</div>
          )}
        </div>

        {publishedVersions.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400" data-testid="rollout-no-versions">
            {/* The never-released state, said without "live" (task 871). */}
            Nothing released yet — this AI Service serves its working state. Publish it to get a
            version you can release.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="rollout-versions">
            {publishedVersions.map((version: ResourceVersionSummary) => {
              const isExpanded = expanded === version.id;
              const entries = flattenPinSet(version.pinSet);

              return (
                <li key={version.id} className="px-4 py-3" data-testid="rollout-version">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setExpanded(isExpanded ? null : version.id)}
                      aria-expanded={isExpanded}
                      data-testid="rollout-version-toggle"
                      className="flex min-w-0 items-start gap-2 text-left"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                      )}
                      <span className="min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          v{version.version}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          {version.publishedAt ? formatRelativeTime(version.publishedAt) : 'unpublished'}
                          {version.publishedBy ? (
                            <>
                              {' · '}
                              <UserName userId={version.publishedBy} />
                            </>
                          ) : ''}
                        </span>
                        {version.notes && (
                          <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                            {version.notes}
                          </span>
                        )}
                      </span>
                    </button>

                    {/* SERVING ONLY (task 832). One quiet badge per lane
                        currently serving this version, and nothing at all for
                        the rest — a version row states what is true, it does not
                        offer work. The deploy that used to live here is the
                        lane's version picker above.

                        AND IT SAYS `Released: / v1.0` (task 871, kv's wording).
                        The badge used to read "Serving {name}", which named the
                        lane by its free-text label and left the version to be
                        read off the row beside it. It now states the whole fact
                        in one phrase, addressed by PATH — `/` is the bare URL,
                        the same identity every release is written against — so
                        the words "live" and "managed" have nothing left to
                        describe here. */}
                    <div className="flex flex-shrink-0 flex-wrap gap-1">
                      {lanes
                        .filter((lane) => laneServing(lane) === version.version)
                        .map((lane) => (
                          <span
                            key={lane.key}
                            data-testid="rollout-version-serving"
                            data-environment={lane.name}
                            data-path={lane.path}
                            className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200"
                          >
                            Released: {lane.path} v{version.version}
                          </span>
                        ))}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 pl-6" data-testid="rollout-version-pinset">
                      {entries.length > 0 ? (
                        <PinSetList entries={entries} testIdPrefix="rollout-pin-set" />
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          No pin set recorded for this version.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* THE RELEASE LOG (task 832). The history behind the promotion arrows,
          finally shown: every deploy this AI Service has had, newest first —
          version, environment, who did it, when, and any note they left. Last on
          the page because that is the reading order — what serves now, what is
          published, what happened. */}
      <div
        data-testid="release-log"
        className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Release log</h3>
        </div>

        {releaseLog.length === 0 ? (
          <p
            data-testid="release-log-empty"
            className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400"
          >
            Nothing has been deployed yet. Every deploy is recorded here — version, environment, who
            released it and when.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {releaseLog.map((row) => {
              const label = row.resourceVersion ? versionLabel(row.resourceVersion) : null;
              const environmentName =
                environmentNameById[row.environmentId] ?? row.environment?.name ?? null;

              return (
                <li
                  key={row.id}
                  data-testid="release-log-row"
                  data-environment={environmentName ?? undefined}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-2.5"
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {label ? `v${label}` : 'Unknown version'}
                  </span>
                  <ArrowRightIcon
                    aria-hidden="true"
                    className="h-3.5 w-3.5 flex-shrink-0 self-center text-gray-400"
                  />
                  <span className="font-mono text-sm text-gray-900 dark:text-white">
                    {/* A deleted environment leaves its history behind — say so
                        rather than render a gap where a name should be. */}
                    {environmentName ?? 'removed environment'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {row.createdBy ? (
                      <>
                        {'by '}
                        <UserName userId={row.createdBy} />
                        {' · '}
                      </>
                    ) : ''}
                    {formatRelativeTime(row.createdAt)}
                  </span>
                  {row.notes && (
                    <p className="w-full text-xs text-gray-600 dark:text-gray-400">{row.notes}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${ACTION_COPY[pending.kind].title} version`}
            data-testid="rollout-confirm-dialog"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {ACTION_COPY[pending.kind].title} v{pending.version} to {pending.lane.name}?
            </h3>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              {pending.lane.synthesized
                ? SYNTHESIZED_LANE_BODY
                : ACTION_COPY[pending.kind].body}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPending(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 dark:border-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeploy}
                disabled={createDeployment.isPending}
                data-testid="rollout-confirm-deploy"
                className="rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {ACTION_COPY[pending.kind].verb}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default ReleasesView;
